import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { listAPBills, createAPBill, approveAPBill, recordAPBillPayment, listSuppliers, listGLAccounts, fetchCreditorAging } from '../api';
import { formatCurrency } from '../utils/currency';

const STATUS_COLORS = {
  DRAFT: 'bg-gray-100 text-gray-600',
  APPROVED: 'bg-blue-100 text-blue-700',
  PARTIALLY_PAID: 'bg-amber-100 text-amber-700',
  PAID: 'bg-green-100 text-green-700',
  VOIDED: 'bg-red-100 text-red-600',
  REVERSED: 'bg-gray-300 text-gray-700',
  OVERDUE: 'bg-red-100 text-red-600',
};

const VAT_TREATMENT_OPTIONS = [
  { value: 'STANDARD', label: 'Standard (VAT applies)' },
  { value: 'EXEMPT', label: 'VAT Exempt' },
  { value: 'ZERO_RATED', label: 'Zero-Rated' },
];

function CreditorManagement({ authAxios, showFormMessage, userRole }) {
  const [bills, setBills] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [aging, setAging] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [activeView, setActiveView] = useState('bills'); // bills | aging
  const [showBillForm, setShowBillForm] = useState(false);
  const [showPayForm, setShowPayForm] = useState(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [payAmount, setPayAmount] = useState('');
  const [payMethod, setPayMethod] = useState('BANK_TRANSFER');
  const [payRef, setPayRef] = useState('');
  const [formErrors, setFormErrors] = useState({});
  const [formData, setFormData] = useState({
    supplier: '', date_received: new Date().toISOString().slice(0, 10), due_date: '', bill_number: '', notes: '',
    vat_input_method: 'AMOUNT',
    items: [{ description: '', gl_account: '', quantity: 1, unit_price: '', tax_amount: '0', vat_treatment: 'STANDARD' }],
    receipt_file: null,
  });

  const canManage = useMemo(() => ['PARTNER', 'SUPERUSER', 'ACCOUNTANT'].includes(userRole), [userRole]);

  const fetchBills = useCallback(async () => {
    setIsLoading(true);
    try {
      const params = {};
      if (statusFilter) params.status = statusFilter;
      const data = await listAPBills(authAxios, params);
      setBills(Array.isArray(data) ? data : []);
    } catch { showFormMessage('error', 'Failed to load bills.'); }
    finally { setIsLoading(false); }
  }, [authAxios, showFormMessage, statusFilter]);

  const fetchRef = useCallback(async () => {
    try {
      const [s, a] = await Promise.all([listSuppliers(authAxios, { is_active: true }), listGLAccounts(authAxios, { is_active: true })]);
      setSuppliers(Array.isArray(s) ? s : []);
      setAccounts(Array.isArray(a) ? a : []);
    } catch {}
  }, [authAxios]);

  const fetchAgingReport = useCallback(async () => {
    try {
      const data = await fetchCreditorAging(authAxios);
      setAging(data);
    } catch { showFormMessage('error', 'Failed to load aging report.'); }
  }, [authAxios, showFormMessage]);

  useEffect(() => { fetchBills(); fetchRef(); }, [fetchBills, fetchRef]);
  useEffect(() => { if (activeView === 'aging') fetchAgingReport(); }, [activeView, fetchAgingReport]);

  const addItem = () => setFormData(f => ({ ...f, items: [...f.items, { description: '', gl_account: '', quantity: 1, unit_price: '', tax_amount: '0', vat_treatment: 'STANDARD' }] }));
  const removeItem = (idx) => setFormData(f => ({ ...f, items: f.items.filter((_, i) => i !== idx) }));
  const updateItem = (idx, field, value) => setFormData(f => {
    const items = [...f.items];
    items[idx] = { ...items[idx], [field]: value };
    return { ...f, items };
  });

  const billTotal = useMemo(() => formData.items.reduce((s, it) => s + ((parseFloat(it.unit_price) || 0) * (parseInt(it.quantity) || 0)) + (parseFloat(it.tax_amount) || 0), 0), [formData.items]);

  const handleSubmitBill = async (e) => {
    e.preventDefault();
    setFormErrors({});
    try {
      const isPercentage = formData.vat_input_method === 'PERCENTAGE';
      const payload = {
        supplier: parseInt(formData.supplier),
        date_received: formData.date_received,
        due_date: formData.due_date || null,
        bill_number: formData.bill_number,
        notes: formData.notes,
        vat_input_method: formData.vat_input_method,
        items: formData.items.filter(i => i.description && i.unit_price).map(i => {
          const item = {
            description: i.description,
            gl_account: i.gl_account ? parseInt(i.gl_account) : null,
            quantity: parseInt(i.quantity) || 1,
            unit_price: parseFloat(i.unit_price),
            vat_treatment: i.vat_treatment || 'STANDARD',
          };
          if (isPercentage) {
            item.tax_percentage = parseFloat(i.tax_amount) || 0;
          } else {
            item.tax_amount = parseFloat(i.tax_amount) || 0;
          }
          return item;
        }),
      };
      // If receipt_file present use FormData
      if (formData.receipt_file) {
        const fd = new FormData();
        Object.entries(payload).forEach(([k, v]) => {
          if (k === 'items') fd.append('items_input', JSON.stringify(v));
          else if (v !== null && v !== undefined) fd.append(k, typeof v === 'object' ? JSON.stringify(v) : v);
        });
        fd.append('receipt_file', formData.receipt_file);
        await createAPBill(authAxios, fd);
      } else {
        await createAPBill(authAxios, payload);
      }
      showFormMessage('success', 'Bill created.');
      setShowBillForm(false);
      fetchBills();
    } catch (err) {
      const data = err.response?.data;
      if (data && typeof data === 'object' && !data.detail) {
        // Field-level errors from API
        setFormErrors(data);
        const msgs = Object.entries(data).map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(', ') : v}`);
        showFormMessage('error', msgs.join(' | '));
      } else {
        showFormMessage('error', data?.detail || 'Failed to create bill.');
      }
    }
  };

  const handleApprove = async (id) => {
    try {
      await approveAPBill(authAxios, id);
      showFormMessage('success', 'Bill approved.');
      fetchBills();
    } catch (err) {
      showFormMessage('error', err.response?.data?.detail || 'Failed to approve.');
    }
  };

  const handlePay = async (billId) => {
    try {
      await recordAPBillPayment(authAxios, billId, { amount: payAmount, payment_method: payMethod, reference: payRef });
      showFormMessage('success', 'Payment recorded.');
      setShowPayForm(null);
      setPayAmount(''); setPayMethod('BANK_TRANSFER'); setPayRef('');
      fetchBills();
    } catch (err) {
      showFormMessage('error', err.response?.data?.detail || 'Failed to record payment.');
    }
  };

  const filtered = useMemo(() => {
    if (!searchTerm) return bills;
    const term = searchTerm.toLowerCase();
    return bills.filter(b => (b.bill_number || '').toLowerCase().includes(term) || (b.supplier_name || b.supplier?.name || '').toLowerCase().includes(term));
  }, [bills, searchTerm]);

  const BUCKET_LABELS = { current: 'Current', '30_days': '1-30 Days', '60_days': '31-60 Days', '90_days': '61-90 Days', over_90: 'Over 90 Days' };

  return (
    <div className="animate__animated animate__fadeIn">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-6 gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">Creditor Management</h2>
          <p className="text-gray-500 text-sm mt-1">Accounts payable, bills and aging</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setActiveView('bills')} className={`px-4 py-2 rounded-lg text-sm font-medium ${activeView === 'bills' ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-700'}`}>Bills</button>
          <button onClick={() => setActiveView('aging')} className={`px-4 py-2 rounded-lg text-sm font-medium ${activeView === 'aging' ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-700'}`}>Aging Report</button>
          {canManage && (
            <button onClick={() => setShowBillForm(true)} className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 text-sm font-medium">+ New Bill</button>
          )}
        </div>
      </div>

      {/* BILLS VIEW */}
      {activeView === 'bills' && (
        <>
          <div className="flex flex-col sm:flex-row gap-3 mb-4">
            <input type="text" placeholder="Search bills..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500" />
            <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm">
              <option value="">All Statuses</option>
              <option value="DRAFT">Draft</option>
              <option value="APPROVED">Approved</option>
              <option value="PARTIALLY_PAID">Partially Paid</option>
              <option value="PAID">Paid</option>
              <option value="REVERSED">Reversed</option>
              <option value="VOIDED">Voided</option>
            </select>
          </div>

          {isLoading ? (
            <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div></div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-12 bg-white rounded-xl shadow-sm"><p className="text-gray-500">No bills found.</p></div>
          ) : (
            <div className="bg-white rounded-xl shadow-sm overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Bill #</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Supplier</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Bill Date</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Due Date</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Total</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Balance</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Receipt</th>
                    {canManage && <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filtered.map(b => (
                    <tr key={b.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 font-mono text-indigo-600">{b.bill_number}</td>
                      <td className="px-4 py-3 text-gray-800">{b.supplier_name || b.supplier?.name || ''}</td>
                      <td className="px-4 py-3 text-gray-600">{b.date_received}</td>
                      <td className="px-4 py-3 text-gray-600">{b.due_date || '—'}</td>
                      <td className="px-4 py-3 text-right font-medium">{formatCurrency(b.total_amount || 0)}</td>
                      <td className="px-4 py-3 text-right font-medium text-red-600">{formatCurrency(b.balance_due || 0)}</td>
                      <td className="px-4 py-3 text-center">
                        <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[b.status] || 'bg-gray-100 text-gray-600'}`}>
                          {(b.status || '').replace(/_/g, ' ')}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        {b.receipt_file ? (
                          <a href={b.receipt_file} target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:text-indigo-800 text-xs font-medium underline">View</a>
                        ) : <span className="text-gray-400 text-xs">—</span>}
                      </td>
                      {canManage && (
                        <td className="px-4 py-3 text-right space-x-2">
                          {b.status === 'DRAFT' && <button onClick={() => handleApprove(b.id)} className="text-green-600 hover:text-green-800 text-xs font-medium">Approve</button>}
                          {['APPROVED', 'PARTIALLY_PAID'].includes(b.status) && (
                            <button onClick={() => { setShowPayForm(b.id); setPayAmount(''); }} className="text-indigo-600 hover:text-indigo-800 text-xs font-medium">Pay</button>
                          )}
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {/* AGING VIEW */}
      {activeView === 'aging' && (
        aging ? (
          <div className="space-y-6">
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
              {Object.entries(BUCKET_LABELS).map(([key, label]) => (
                <div key={key} className="bg-white rounded-xl shadow-sm p-4 text-center">
                  <p className="text-xs text-gray-500 uppercase">{label}</p>
                  <p className="text-lg font-bold text-gray-800 mt-1">{formatCurrency(aging.totals?.[key] || 0)}</p>
                  <p className="text-xs text-gray-400">{(aging.buckets?.[key] || []).length} bills</p>
                </div>
              ))}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="bg-white rounded-xl shadow-sm p-4 text-center">
                <p className="text-sm text-gray-500">Total Outstanding</p>
                <p className="text-2xl font-bold text-red-600">{formatCurrency(aging.grand_total || 0)}</p>
              </div>
              {aging.upcoming_total != null && (
                <div className="bg-white rounded-xl shadow-sm p-4 text-center">
                  <p className="text-sm text-gray-500">Upcoming (Next 30 Days)</p>
                  <p className="text-2xl font-bold text-amber-600">{formatCurrency(aging.upcoming_total)}</p>
                </div>
              )}
            </div>
            {Object.entries(BUCKET_LABELS).map(([key, label]) => {
              const items = aging.buckets?.[key] || [];
              if (items.length === 0) return null;
              return (
                <div key={key}>
                  <h3 className="text-sm font-semibold text-gray-600 mb-2">{label}</h3>
                  <div className="bg-white rounded-xl shadow-sm overflow-hidden">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Bill #</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Supplier</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Due Date</th>
                          <th className="px-4 py-2 text-right text-xs font-medium text-gray-500">Balance</th>
                          <th className="px-4 py-2 text-right text-xs font-medium text-gray-500">Days Overdue</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {items.map((item, i) => (
                          <tr key={i} className="hover:bg-gray-50">
                            <td className="px-4 py-2 font-mono">{item.bill_number}</td>
                            <td className="px-4 py-2">{item.supplier}</td>
                            <td className="px-4 py-2 text-gray-600">{item.due_date || '—'}</td>
                            <td className="px-4 py-2 text-right font-medium text-red-600">{formatCurrency(item.balance)}</td>
                            <td className="px-4 py-2 text-right text-gray-600">{item.days_overdue}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div></div>
        )
      )}

      {/* New Bill Form */}
      {showBillForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-3xl w-full p-6 max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-semibold mb-4">New Bill</h3>
            <form onSubmit={handleSubmitBill} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Supplier *</label>
                  <select required value={formData.supplier} onChange={e => setFormData({ ...formData, supplier: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm">
                    <option value="">Select Supplier</option>
                    {suppliers.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Bill Number *</label>
                  <input type="text" required value={formData.bill_number} onChange={e => setFormData({ ...formData, bill_number: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" placeholder="e.g. INV-001" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Bill Date</label>
                  <input type="date" value={formData.date_received} onChange={e => setFormData({ ...formData, date_received: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Due Date</label>
                  <input type="date" value={formData.due_date} onChange={e => setFormData({ ...formData, due_date: e.target.value })}
                    className={`w-full px-3 py-2 border rounded-lg text-sm ${formErrors.due_date ? 'border-red-400' : 'border-gray-300'}`} />
                  {formErrors.due_date && <p className="text-red-500 text-xs mt-1">{Array.isArray(formErrors.due_date) ? formErrors.due_date[0] : formErrors.due_date}</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">VAT Input Method</label>
                  <select value={formData.vat_input_method} onChange={e => setFormData({ ...formData, vat_input_method: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm">
                    <option value="AMOUNT">Enter VAT Amount</option>
                    <option value="PERCENTAGE">Enter VAT Percentage</option>
                  </select>
                </div>
              </div>
              {formErrors.items && <p className="text-red-500 text-xs">{Array.isArray(formErrors.items) ? formErrors.items[0] : formErrors.items}</p>}

              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-medium text-gray-700">Line Items</label>
                  <button type="button" onClick={addItem} className="text-indigo-600 hover:text-indigo-800 text-xs font-medium">+ Add Item</button>
                </div>
                {formData.items.map((item, idx) => (
                  <div key={idx} className="mb-2">
                    <div className="grid grid-cols-12 gap-2 items-center">
                      <input type="text" placeholder="Description" value={item.description} onChange={e => updateItem(idx, 'description', e.target.value)}
                        className="col-span-3 px-2 py-2 border border-gray-300 rounded-lg text-sm" />
                      <select value={item.gl_account} onChange={e => updateItem(idx, 'gl_account', e.target.value)}
                        className="col-span-2 px-2 py-2 border border-gray-300 rounded-lg text-sm">
                        <option value="">GL Account</option>
                        {accounts.filter(a => a.category === 'EXPENSE').map(a => <option key={a.id} value={a.id}>{a.code} – {a.name}</option>)}
                      </select>
                      <input type="number" min="1" placeholder="Qty" value={item.quantity} onChange={e => updateItem(idx, 'quantity', e.target.value)}
                        className="col-span-1 px-2 py-2 border border-gray-300 rounded-lg text-sm text-right" />
                      <input type="number" step="0.01" placeholder="Unit Price" value={item.unit_price} onChange={e => updateItem(idx, 'unit_price', e.target.value)}
                        className="col-span-2 px-2 py-2 border border-gray-300 rounded-lg text-sm text-right" />
                      <input type="number" step="0.01" placeholder={formData.vat_input_method === 'PERCENTAGE' ? 'VAT %' : 'VAT Amt'}
                        value={item.tax_amount} onChange={e => updateItem(idx, 'tax_amount', e.target.value)}
                        className="col-span-1 px-2 py-2 border border-gray-300 rounded-lg text-sm text-right"
                        disabled={item.vat_treatment !== 'STANDARD'} />
                      <select value={item.vat_treatment} onChange={e => updateItem(idx, 'vat_treatment', e.target.value)}
                        className="col-span-2 px-2 py-2 border border-gray-300 rounded-lg text-xs">
                        {VAT_TREATMENT_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                      </select>
                      <button type="button" onClick={() => removeItem(idx)} className="col-span-1 text-red-400 hover:text-red-600 text-sm">✕</button>
                    </div>
                  </div>
                ))}
                <div className="text-right text-sm font-medium mt-1">Total: {formatCurrency(billTotal)}</div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Receipt / Invoice File (optional)</label>
                <input type="file" accept="image/*,.pdf" onChange={e => setFormData({ ...formData, receipt_file: e.target.files[0] || null })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                <textarea value={formData.notes} onChange={e => setFormData({ ...formData, notes: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" rows={2} />
              </div>

              <div className="flex justify-end gap-2">
                <button type="button" onClick={() => setShowBillForm(false)} className="px-4 py-2 text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 text-sm">Cancel</button>
                <button type="submit" className="px-4 py-2 bg-indigo-600             text-white rounded-lg hover:bg-indigo-700 text-sm">Create Bill</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Payment Modal */}
      {showPayForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold mb-4">Record Payment</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Amount (BWP)</label>
                <input type="number" step="0.01" min="0.01" value={payAmount} onChange={e => setPayAmount(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" autoFocus />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Payment Method</label>
                <select value={payMethod} onChange={e => setPayMethod(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm">
                  <option value="BANK_TRANSFER">Bank Transfer / EFT</option>
                  <option value="CHEQUE">Cheque</option>
                  <option value="CASH">Cash</option>
                  <option value="CARD">Card</option>
                  <option value="OTHER">Other</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Reference</label>
                <input type="text" value={payRef} onChange={e => setPayRef(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
              </div>
              <div className="flex justify-end gap-2">
                <button onClick={() => setShowPayForm(null)} className="px-4 py-2 text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 text-sm">Cancel</button>
                <button onClick={() => handlePay(showPayForm)} className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm">Record Payment</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default CreditorManagement;
